import { useCssVars as _useCssVars, defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = ["id"]
const _hoisted_2 = { class: "header" }
const _hoisted_3 = { class: "content" }
const _hoisted_4 = {
  key: 0,
  class: "sub-features"
}
const _hoisted_5 = { class: "section-content" }

import { computed, watch, provide, reactive, ref, onMounted, onUnmounted, toRefs } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import FeatureControlSection from '@/design-system/molecules/FeatureControlSection.vue'
import { SharedData, sharedDataKey } from './types'
import useFeatures, { useFeature } from '@/pinia/features'
import { useI18n } from 'vue-i18n'
import FeatureComponent from './FeatureComponent.vue'
import DashboardFeatureIcon from './AnalyticsDashboard/components/DashboardFeatureIcon.vue'
import { LocaleString } from '@/api-client/models/utilization/Contracts'
import { getRealmId } from '@/utils/realmId'
import { storeToRefs } from 'pinia'
import { useGeneralStore } from '@/pinia'
import { useStore } from 'vuex'
import { useSelectionStore } from '@/pinia/selection'
import { PoiProperties } from '@/interfaces/poi.interface'
import { PoiInterface as navVisPoi } from '@navvis/ivion'
import { Feature } from '@/pinia/feature'

type Props = {
  title: string
  subtitle: LocaleString
  featureName: string
  enableFullscreen?: boolean
  size?: 'small' | 'medium' | 'large'
}


export default /*@__PURE__*/_defineComponent({
  __name: 'FeatureCard',
  props: {
    title: {},
    subtitle: {},
    featureName: {},
    enableFullscreen: { type: Boolean, default: true },
    size: { default: 'medium' }
  },
  setup(__props: any) {

_useCssVars(_ctx => ({
  "035ccf96": (width.value)
}))

const props = __props

const route = useRoute()
const router = useRouter()
const { locale } = useI18n()
const { feature: overviewFeature } = useFeature(`home/${props.featureName}`)
const { feature: subFeature } = useFeature(`home/${props.featureName}/*/*`)
const features = useFeatures()
const { resetMainFeature, pushFeatures, setFeature } = features
const { activeFeature } = toRefs(features)
const piniaStore = useGeneralStore()
const store = useStore()
const selectionStore = useSelectionStore()
const children = computed(() => overviewFeature.value?.children?.filter((c) => c.enabled) || [])
const { clickedEntityType, currentEntity } = storeToRefs(selectionStore)
const realmId = getRealmId()

const isPoiFromBackend =
  !(store.state.selectedPoi as unknown as navVisPoi)?.matching_title &&
  !(store.state.selectedPoi as unknown as navVisPoi)?.pov

const width = computed(() => {
  switch (props.size) {
    case 'small':
      return '341px'
    case 'medium':
      return '539px'
    case 'large':
      return '539px'
  }
  return '539px'
})

const title = computed(() => {
  if (props.featureName === 'info') {
    if (clickedEntityType.value === 'REGION') {
      return realmId
    }
    if (clickedEntityType.value === 'POI') {
      return store.state.selectedPoi ? poiTitle(isPoiFromBackend, store.state.selectedPoi) : ''
    }
    if (clickedEntityType.value === 'LINE') {
      return piniaStore.selectedLine?.name ?? ''
    }
    if (clickedEntityType.value === 'ZONE') {
      return piniaStore.selectedZone?.name ?? ''
    }
    return currentEntity.value?.displayName ?? ''
  }
  if (router.currentRoute.value.path === `/home/${props.featureName}`) {
    return overviewFeature.value?.title?.[locale.value] ?? ''
  }
  return subFeature.value?.title[locale.value] ?? ''
})

const info = computed(() => {
  return subFeature.value?.info?.[locale.value] ?? ''
})

const sharedData = reactive<SharedData>({
  fullscreen: false,
  sectionIndex: 0,
  updateSectionIndex
})
provide(sharedDataKey, sharedData)

const showResizeButton = ref(false)
const showBackButton = ref(false)
const abortController = new AbortController()

function poiTitle(isPoiFromBackend: boolean, selectedPoi: PoiProperties): string {
  if (!isPoiFromBackend) return selectedPoi.title ?? ''
  return selectedPoi.name ? selectedPoi.name : selectedPoi.title ?? ''
}

const allChildren = computed<Feature[]>(() => {
  return children.value.flatMap((c) => c.children).filter((c) => c !== undefined) as Feature[]
})

watch(
  router.currentRoute,
  (value) => {
    if (value.path === `/home/${props.featureName}`) {
      showBackButton.value = false
      return
    }
    const children = allChildren.value?.filter((c) => c.enabled)
    if (children?.length === 1) {
      showBackButton.value = false
      return
    }
    showBackButton.value = true
  },
  { immediate: true }
)

function toggleFullscreen() {
  sharedData.fullscreen = !sharedData.fullscreen
  piniaStore.setIsFeatureFullscreen(sharedData.fullscreen)
}

function exit() {
  resetMainFeature()
}

onMounted(() => {
  sharedData.fullscreen = window.innerWidth <= 1024
  piniaStore.setIsFeatureFullscreen(sharedData.fullscreen)
  showResizeButton.value = window.innerWidth > 1024
  window.addEventListener('resize', () => resizeEventHandler(), { signal: abortController.signal })
  if (activeFeature.value?.name === props.featureName) {
    if (allChildren.value.length === 1 && allChildren.value[0].name) {
      pushFeatures([children.value[0].name, allChildren.value[0].name])
    }
  }
})

onUnmounted(() => {
  abortController.abort()
})

const mobileScreen = computed(() => {
  return store.state.mobileScreen
})

const computedClass = computed(() => {
  return {
    fullscreen: sharedData.fullscreen,
    mobile: mobileScreen.value === true
  }
})

function resizeEventHandler() {
  showResizeButton.value = window.innerWidth > 1024
  if (!sharedData.fullscreen) sharedData.fullscreen = window.innerWidth <= 1024
  piniaStore.setIsFeatureFullscreen(sharedData.fullscreen)
}

function updateSectionIndex(index: number) {
  sharedData.sectionIndex = index
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("article", {
    id: `${_ctx.featureName}-feature`,
    class: _normalizeClass(['feature-card', computedClass.value])
  }, [
    _createElementVNode("section", _hoisted_2, [
      _createVNode(FeatureControlSection, {
        title: title.value,
        fullscreen: sharedData.fullscreen,
        "show-back-button": showBackButton.value,
        "show-resize-button": showResizeButton.value && _ctx.enableFullscreen,
        info: info.value,
        onToggleFullscreen: toggleFullscreen,
        onExit: exit,
        onGoBack: _cache[0] || (_cache[0] = () => _unref(setFeature)(`home/${_ctx.featureName}`))
      }, null, 8, ["title", "fullscreen", "show-back-button", "show-resize-button", "info"])
    ]),
    _createElementVNode("section", _hoisted_3, [
      (_unref(route).path === `/home/${_ctx.featureName}`)
        ? (_openBlock(), _createElementBlock("section", _hoisted_4, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(children.value, (feature) => {
              return (_openBlock(), _createElementBlock("section", {
                key: feature.name,
                class: "sub-section"
              }, [
                _createElementVNode("h1", null, _toDisplayString(feature.title[_unref(locale)]), 1),
                _createElementVNode("div", _hoisted_5, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(feature.children, (feat) => {
                    return (_openBlock(), _createBlock(DashboardFeatureIcon, {
                      key: feat.name,
                      id: `${feat.name}-sub-feature`,
                      caption: feat.titleShort?.[_unref(locale)] ?? feat.title[_unref(locale)],
                      features: [feature.name, feat.name]
                    }, {
                      default: _withCtx(() => [
                        (feat.props)
                          ? (_openBlock(), _createBlock(_resolveDynamicComponent(feat.props.icon), { key: 0 }))
                          : _createCommentVNode("", true)
                      ]),
                      _: 2
                    }, 1032, ["id", "caption", "features"]))
                  }), 128))
                ])
              ]))
            }), 128))
          ]))
        : _createCommentVNode("", true),
      _createVNode(FeatureComponent, {
        feature: `home/${_ctx.featureName}/*/*`
      }, null, 8, ["feature"])
    ])
  ], 10, _hoisted_1))
}
}

})